/**
 * ConfigurationFreshnessBanner
 *
 * A compact global status bar mounted in the app shell layout. It polls the
 * configuration version endpoint every 10 s (while the window is focused) and
 * shows saved configuration state from that single source — no mutation onSuccess
 * handlers are modified across the app.
 *
 * State derivation:
 * - When the polled version number increases, a transient "Configuration
 *   updated" highlight is shown (~5 s).
 * - The propagation message depends on the last changeType:
 *     • engine-restart types (routing, sip_config, network, sip_interface,
 *       certificate, host, logging, full_reload):
 *       application unconfirmed (the endpoint supplies no engine acknowledgment)
 *     • cache-ttl types (sip_peer, sip_peers, codec, peer):
 *       expected propagation within ~60 seconds, explicitly unconfirmed
 * - For cache-ttl types, a live "propagating…" indicator is shown while
 *   now − changedAt < 60 s. Expiry never establishes successful application.
 * - A "Force full reload" button opens a confirmation modal and calls
 *   POST /api/configuration/reload.
 */

import { useState, useEffect, useRef } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle2, Loader2, Zap } from 'lucide-react';
import { useConfigurationVersion, useTriggerFullReload } from '@/hooks/useConfigurationVersion';
import { getPropagationMode, isPropagating, CACHE_TTL_SECONDS } from '@/lib/configuration-propagation';
import { Modal } from '@/components/common/modals';
import toast from 'react-hot-toast';

/** How long the "Configuration updated" highlight stays visible after a version bump. */
const JUST_UPDATED_TIMEOUT_MS = 5000;

const DEFAULT_RELOAD_REASON = 'Manual full reload from web console';

/** Human-readable label for a changeType. */
function changeTypeLabel(changeType: string): string {
  const labels: Record<string, string> = {
    sip_peer: 'SIP peer',
    sip_peers: 'peer group',
    codec: 'codec',
    peer: 'peer',
    routing: 'routing rule',
    sip_config: 'SIP config',
    sip_interface: 'SIP interface',
    network: 'network',
    certificate: 'certificate',
    host: 'host',
    logging: 'logging',
    full_reload: 'full reload',
  };
  return labels[changeType] ?? changeType;
}

/** Format an ISO timestamp as a short relative string. */
function formatRelativeTime(isoTimestamp: string, now: number): string {
  const elapsedMs = Math.max(0, now - new Date(isoTimestamp).getTime());
  const seconds = Math.floor(elapsedMs / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(isoTimestamp).toLocaleDateString();
}

export function ConfigurationFreshnessBanner() {
  const { data: version, isError } = useConfigurationVersion();
  const triggerReload = useTriggerFullReload();

  const [showConfirm, setShowConfirm] = useState(false);
  const [reloadReason, setReloadReason] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const [justUpdated, setJustUpdated] = useState(false);

  const prevVersionRef = useRef<number | undefined>(undefined);
  const justUpdatedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Detect version increases → transient "Configuration updated" highlight.
  // On the very first load (prevVersionRef undefined) we do NOT trigger the
  // highlight — only on subsequent version changes detected via polling.
  useEffect(() => {
    if (!version) return;
    if (prevVersionRef.current !== undefined && prevVersionRef.current !== version.version) {
      setJustUpdated(true);
      if (justUpdatedTimerRef.current) {
        clearTimeout(justUpdatedTimerRef.current);
      }
      justUpdatedTimerRef.current = setTimeout(() => setJustUpdated(false), JUST_UPDATED_TIMEOUT_MS);
    }
    prevVersionRef.current = version.version;
  }, [version]);

  // Tick "now" every second while a cache-TTL change is actively propagating,
  // so the "propagating…" indicator clears when the TTL window expires.
  const propagating = version
    ? isPropagating(version.changeType, version.lastModified, now)
    : false;

  useEffect(() => {
    if (!propagating) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [propagating]);

  // Also tick "now" while justUpdated so the relative time label stays fresh.
  useEffect(() => {
    if (!justUpdated) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [justUpdated]);

  // Cleanup the just-updated timer on unmount.
  useEffect(() => {
    return () => {
      if (justUpdatedTimerRef.current) {
        clearTimeout(justUpdatedTimerRef.current);
      }
    };
  }, []);

  const mode = version ? getPropagationMode(version.changeType) : null;
  const description = version?.metadata?.reason;
  const isFullReload = version?.changeType === 'full_reload';

  const handleConfirmReload = () => {
    const reason = reloadReason.trim() || DEFAULT_RELOAD_REASON;
    triggerReload.mutate(reason, {
      onSuccess: () => {
        toast.success('Full configuration reload requested — engine application not yet confirmed');
        setShowConfirm(false);
        setReloadReason('');
      },
      onError: (err) => {
        const message = err instanceof Error ? err.message : 'Failed to trigger reload';
        toast.error(message);
      },
    });
  };

  // ---- Visual state ----

  if (isError) {
    return (
      <div className="border-b border-amber-200 bg-amber-50 px-4 py-1.5 sm:px-6 md:px-8">
        <div className="mx-auto flex max-w-7xl items-center gap-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Unable to check configuration status</span>
        </div>
      </div>
    );
  }

  if (!version || !mode) {
    return (
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-1.5 sm:px-6 md:px-8">
        <div className="mx-auto flex max-w-7xl items-center gap-2 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
          <span>Checking configuration…</span>
        </div>
      </div>
    );
  }

  const isActive = justUpdated || propagating;
  const isEngineRestartActive = justUpdated && mode === 'engine-restart';

  // Banner background depends on state
  const bannerClass = isEngineRestartActive
    ? 'border-b border-blue-200 bg-blue-50'
    : propagating
      ? 'border-b border-blue-200 bg-blue-50'
      : justUpdated
        ? 'border-b border-emerald-200 bg-emerald-50'
        : 'border-b border-gray-200 bg-gray-50';

  // Propagation message
  let propagationMessage: string;
  let propagationIcon: React.ReactNode;

  if (mode === 'engine-restart') {
    propagationMessage = isFullReload
      ? 'Full reload requested — engine application unconfirmed'
      : 'Configuration saved — engine application unconfirmed';
    propagationIcon = isEngineRestartActive
      ? <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
      : <RefreshCw className="h-3.5 w-3.5 text-blue-500" />;
  } else {
    // cache-ttl
    if (propagating) {
      propagationMessage = `Expected propagation within ~${CACHE_TTL_SECONDS}s — unconfirmed`;
      propagationIcon = <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />;
    } else {
      propagationMessage = 'Configuration saved — engine application unconfirmed';
      propagationIcon = <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
    }
  }

  return (
    <>
      <div className={`${bannerClass} px-4 py-1.5 sm:px-6 md:px-8`}>
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          {/* Left: version + last change */}
          <div className="flex items-center gap-2 min-w-0">
            {isActive ? (
              <Zap className="h-4 w-4 flex-shrink-0 text-blue-600" />
            ) : (
              <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-gray-400" />
            )}
            <span className={`font-medium ${isActive ? 'text-blue-900' : 'text-gray-600'}`}>
              Saved config v{version.version}
            </span>
            <span className="text-gray-400">·</span>
            <span className="text-gray-500 truncate">
              last: {changeTypeLabel(version.changeType)}
              {version.operation ? ` ${version.operation}` : ''} {formatRelativeTime(version.lastModified, now)}
            </span>
          </div>

          {/* Description (reason) — hidden on small screens */}
          {description && (
            <span className="hidden text-gray-400 truncate lg:inline">
              {description}
            </span>
          )}

          {/* Right: propagation message + force reload button */}
          <div className="ml-auto flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-gray-500">
              {propagationIcon}
              <span className="hidden sm:inline">{propagationMessage}</span>
            </span>
            <button
              onClick={() => setShowConfirm(true)}
              disabled={triggerReload.isPending}
              className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
               title="Request a full configuration reload; completion must be verified on the engine"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${triggerReload.isPending ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Force full reload</span>
              <span className="sm:hidden">Reload</span>
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation modal */}
      <Modal
        isOpen={showConfirm}
        onClose={() => {
          if (!triggerReload.isPending) {
            setShowConfirm(false);
            setReloadReason('');
          }
        }}
        title="Force Full Reload"
        subtitle="Request an engine reload of saved configuration"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowConfirm(false);
                setReloadReason('');
              }}
              disabled={triggerReload.isPending}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmReload}
              disabled={triggerReload.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {triggerReload.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Triggering…
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Trigger reload
                </>
              )}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-500 mt-0.5" />
            <p className="text-sm text-gray-700">
              A full reload requests that the engine reload configuration from the database.
              <strong> Active calls may be affected.</strong> A successful request confirms
              the saved reload signal, not engine completion. Check engine status afterward.
            </p>
          </div>
          <div>
            <label
              htmlFor="reload-reason"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Reason <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="reload-reason"
              type="text"
              value={reloadReason}
              onChange={(e) => setReloadReason(e.target.value)}
              placeholder={DEFAULT_RELOAD_REASON}
              disabled={triggerReload.isPending}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none disabled:bg-gray-100"
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
